import { FC, MouseEvent, useEffect, useMemo, useState } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import {
  DataTable,
  InlineLoading,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  TableToolbar,
  TableToolbarContent,
  TableToolbarSearch,
  Tile,
} from '@carbon/react';
import { StatusTag, type PartyStatus } from 'components/status-tag';
import type { CostOverviewWell } from './types-cost';
import {
  costOverviewDataAtom,
  costOverviewLoadingAtom,
  costOverviewUrl,
  editWorkingInterestAtom,
  fetchCostOverviewAtom,
} from './atoms/costs';
import { WellWorkflowStatusBadge } from './well-workflow-status-badge';
import {
  formatCurrency,
  formatHoldFlag,
  formatWorkingInterest5,
} from './cost-utils';
import shell from './structuring-module.module.scss';
import styles from './cost.module.scss';

// Ported from the mock cost overview (aggregate tiles + per-well table). Row
// click drills into Structure Costs for that well (keyed by afeHeaderId).
// Mirrors `wells-overview-section.tsx` (module-level overview atom + DataTable).

interface Props {
  documentId: number | string;
  /** Drill into Structure Costs for a well (afeHeaderId). */
  onSelectWell: (afeHeaderId: number) => void;
}

const HEADERS = [
  { key: 'no', header: 'No.' },
  { key: 'status', header: 'Status' },
  { key: 'uwi', header: 'UWI' },
  { key: 'well', header: 'Well' },
  { key: 'afeId', header: 'AFE ID' },
  { key: 'wi', header: 'Working Interest %' },
  { key: 'drilling', header: 'Drilling' },
  { key: 'completion', header: 'Completion' },
  { key: 'facilities', header: 'Facilities' },
  { key: 'grandTotal', header: 'Grand Total' },
  { key: 'netAfeCost', header: 'Net AFE Cost' },
  { key: 'holdFlag', header: 'Hold Flag' },
];

// Editable Working Interest % field (v2 mock). Commits on blur via the catalog
// AfeHeader update (PATCH /afe_headers/:id); the parent reloads the cost
// overview so the server-recomputed Net AFE Cost flows back in.
const WorkingInterestField: FC<{
  afeHeaderId: number;
  value: number | null;
  onSaved: () => void;
}> = ({ afeHeaderId, value, onSaved }) => {
  const editWorkingInterest = useSetAtom(editWorkingInterestAtom);
  const [draft, setDraft] = useState(formatWorkingInterest5(value));
  const [saving, setSaving] = useState(false);

  const commit = () => {
    const trimmed = draft.trim();
    const parsed = trimmed === '' ? null : Number(trimmed);
    if (parsed !== null && Number.isNaN(parsed)) {
      setDraft(formatWorkingInterest5(value)); // revert invalid input
      return;
    }
    if (parsed === value) return;
    setSaving(true);
    editWorkingInterest({
      afeHeaderId,
      workingInterestPercentage: parsed,
      onSuccess: () => {
        setSaving(false);
        onSaved();
      },
      onError: () => setSaving(false),
    });
  };

  return (
    <span className={styles.wiField}>
      <input
        className={styles.wiInput}
        type="text"
        inputMode="decimal"
        value={draft}
        aria-label="Working Interest %"
        disabled={saving}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
        }}
        onClick={(e) => e.stopPropagation()}
      />
      <span className={styles.wiSuffix}>%</span>
      {saving && (
        <InlineLoading
          className={styles.wiLoader}
          description="Saving…"
          aria-label="Saving working interest"
        />
      )}
    </span>
  );
};

export const CostOverviewSection: FC<Props> = ({
  documentId,
  onSelectWell,
}) => {
  const data = useAtomValue(costOverviewDataAtom);
  const loading = useAtomValue(costOverviewLoadingAtom);
  const reload = useSetAtom(fetchCostOverviewAtom);

  const href = costOverviewUrl(documentId);

  useEffect(() => {
    reload(href);
  }, [href, reload]);

  // Memoize so the `?? []` fallback doesn't produce a fresh array reference on
  // every render, which would otherwise invalidate the `wellsById` useMemo below.
  const wells = useMemo(() => data?.wells ?? [], [data?.wells]);
  const summary = data?.summary;
  // Section status mirrors the v2 mock's getCostSectionStatus: not-started when
  // nothing is complete, complete when every active well is complete, else
  // in-progress. (The previous `completeCount === totalCount` check wrongly
  // reported "Complete (0/0)" before any well had been reviewed.)
  const activeWells = wells.filter((w) => !w.removed);
  const completeCount = activeWells.filter(
    (w) => w.status === 'complete'
  ).length;
  const totalCount = activeWells.length;
  const sectionStatus: PartyStatus =
    completeCount === 0
      ? 'not_started'
      : completeCount === totalCount
        ? 'complete'
        : 'in_progress';

  // The cost summary endpoint doesn't aggregate Contingencies & Overhead / Other
  // (they live per-well), so sum them here — the v2 mock only shows these tiles
  // when their total is > 0. Removed wells are excluded to match the server-side
  // tiles (build_summary drops removed rows).
  const contingenciesTotal = activeWells.reduce(
    (sum, w) => sum + (w.contingencies ?? 0),
    0
  );
  const otherTotal = activeWells.reduce((sum, w) => sum + (w.other ?? 0), 0);

  const rows = wells.map((w) => ({
    id: String(w.afeHeaderId),
    no: '',
    status: '',
    uwi: w.uwi ?? '—',
    well: w.wellName ?? '—',
    afeId: String(w.afeHeaderId),
    wi: '',
    drilling: '',
    completion: '',
    facilities: '',
    grandTotal: '',
    netAfeCost: '',
    holdFlag: formatHoldFlag(w.heldFlag),
  }));

  // Build a single afeHeaderId → well lookup so each rendered row resolves its
  // well via `.get` (O(1)) instead of a per-row `wells.find` (O(n)).
  const wellsById = useMemo(() => {
    const map = new Map<string, CostOverviewWell>();
    wells.forEach((w) => map.set(String(w.afeHeaderId), w));
    return map;
  }, [wells]);

  return (
    <div className={shell.overviewBody}>
      <div className={shell.overviewSectionTitleRow}>
        <h4 className={shell.overviewSectionTitle}>Cost Information</h4>
        {summary && (
          <StatusTag
            status={sectionStatus}
            progress={`${completeCount}/${totalCount}`}
          />
        )}
      </div>

      {summary && (
        <div className={shell.summaryTiles}>
          <Tile>
            <p className={shell.tileCount}>
              {formatCurrency(summary.grandTotal)}
            </p>
            <p className={shell.tileLabel}>Grand Total</p>
          </Tile>
          <Tile>
            <p className={shell.tileCount}>
              {formatCurrency(summary.drilling)}
            </p>
            <p className={shell.tileLabel}>Drilling</p>
          </Tile>
          <Tile>
            <p className={shell.tileCount}>
              {formatCurrency(summary.completion)}
            </p>
            <p className={shell.tileLabel}>Completion</p>
          </Tile>
          <Tile>
            <p className={shell.tileCount}>
              {formatCurrency(summary.facilities)}
            </p>
            <p className={shell.tileLabel}>Facilities</p>
          </Tile>
          {contingenciesTotal > 0 && (
            <Tile>
              <p className={shell.tileCount}>
                {formatCurrency(contingenciesTotal)}
              </p>
              <p className={shell.tileLabel}>Contingencies &amp; Overhead</p>
            </Tile>
          )}
          {otherTotal > 0 && (
            <Tile>
              <p className={shell.tileCount}>{formatCurrency(otherTotal)}</p>
              <p className={shell.tileLabel}>Other</p>
            </Tile>
          )}
          <Tile>
            <p className={shell.tileCount}>
              {formatCurrency(summary.netAfeCost)}
            </p>
            <p className={shell.tileLabel}>Net AFE Cost</p>
          </Tile>
        </div>
      )}

      {loading && !data ? (
        <p className={shell.auditLogEmpty}>Loading costs…</p>
      ) : (
        <DataTable rows={rows} headers={HEADERS}>
          {({
            rows: dtRows,
            headers,
            getHeaderProps,
            getTableProps,
            onInputChange,
          }: any) => (
            <TableContainer>
              <TableToolbar size="sm">
                <TableToolbarContent>
                  <TableToolbarSearch
                    onChange={onInputChange}
                    placeholder="Search wells…"
                    persistent
                    size="sm"
                  />
                </TableToolbarContent>
              </TableToolbar>
              <Table {...getTableProps()} size="sm">
                <TableHead>
                  <TableRow>
                    {headers.map((h: any) => (
                      <TableHeader
                        {...getHeaderProps({ header: h })}
                        key={h.key}
                      >
                        {h.header}
                      </TableHeader>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dtRows.map((row: any, rowIndex: number) => {
                    const well = wellsById.get(row.id);
                    if (!well) return null;
                    // Removed wells stay in the table struck through (mirrors the
                    // Structured Wells section): dim the row, suppress the drill-in
                    // click, and show the Removed badge instead of cost progress.
                    const isRemoved = well.removed;
                    return (
                      <TableRow
                        key={row.id}
                        className={isRemoved ? shell.rowRemoved : undefined}
                        onClick={() => {
                          if (!isRemoved) onSelectWell(well.afeHeaderId);
                        }}
                        style={{ cursor: isRemoved ? 'default' : 'pointer' }}
                      >
                        <TableCell>{rowIndex + 1}</TableCell>
                        <TableCell>
                          <WellWorkflowStatusBadge
                            status={isRemoved ? 'removed' : well.status}
                            variant="inline"
                          />
                        </TableCell>
                        <TableCell>
                          <Link
                            href="#"
                            onClick={(e: MouseEvent) => e.preventDefault()}
                          >
                            {well.uwi ?? '—'}
                          </Link>
                        </TableCell>
                        <TableCell>{well.wellName ?? '—'}</TableCell>
                        <TableCell
                          onClick={(e: MouseEvent) => e.stopPropagation()}
                        >
                          <Link href={`/afe_catalog/${well.afeHeaderId}`}>
                            {well.afeHeaderId}
                          </Link>
                        </TableCell>
                        <TableCell
                          onClick={(e: MouseEvent) => e.stopPropagation()}
                        >
                          {isRemoved ? (
                            // A removed well is excluded from the totals, so its
                            // WI is display-only — no inline edit.
                            <span>
                              {formatWorkingInterest5(
                                well.workingInterestPercentage
                              ) || '—'}
                              {well.workingInterestPercentage !== null && '%'}
                            </span>
                          ) : (
                            <WorkingInterestField
                              // Remount when the persisted value changes (after a
                              // save + reload) so the draft re-initializes from the
                              // new value — no prop→state sync effect needed.
                              key={`wi-${well.afeHeaderId}-${
                                well.workingInterestPercentage ?? 'none'
                              }`}
                              afeHeaderId={well.afeHeaderId}
                              value={well.workingInterestPercentage}
                              onSaved={() => reload(href)}
                            />
                          )}
                        </TableCell>
                        <TableCell>{formatCurrency(well.drilling)}</TableCell>
                        <TableCell>{formatCurrency(well.completion)}</TableCell>
                        <TableCell>{formatCurrency(well.facilities)}</TableCell>
                        <TableCell>{formatCurrency(well.grandTotal)}</TableCell>
                        <TableCell>{formatCurrency(well.netAfeCost)}</TableCell>
                        <TableCell>{formatHoldFlag(well.heldFlag)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DataTable>
      )}
    </div>
  );
};
